// src/pages/Equipment/EquipmentForm.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Modal, Form, Input, Select, Row, Col,
  Button, Space, Tag, message, Divider, Typography
} from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { equipmentAPI, employeesAPI, dictAPI } from '../../api/client';

const { Text } = Typography;

const STATUS_COLORS = {
  'На складе':    'blue',
  'Эксплуатация': 'green',
  'Ремонт':       'orange',
  'Обслуживание': 'purple',
  'Списан':       'red',
};

export default function EquipmentForm({ open, onClose }) {
  const [form] = Form.useForm();
  const [prefix, setPrefix]           = useState('K');
  const [categoryId, setCategoryId]   = useState(null);
  const queryClient = useQueryClient();

  // Справочники
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn:  () => dictAPI.getCategories().then(r => r.data),
    enabled:  open,
  });

  const { data: typesData } = useQuery({
    queryKey: ['types-for-category', categoryId],
    queryFn:  () => dictAPI.getTypes(categoryId).then(r => r.data),
    enabled:  open && !!categoryId,
  });

  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn:  () => dictAPI.getLocations().then(r => r.data),
    enabled:  open,
  });

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn:  () => dictAPI.getCompanies().then(r => r.data),
    enabled:  open,
  });

  const { data: employeesData } = useQuery({
    queryKey: ['employees-active'],
    queryFn:  () => employeesAPI.getAll({ is_active: true, limit: 500 }).then(r => r.data),
    enabled:  open,
  });

  // Следующий инвентарный номер
  const { data: nextNumData, refetch: refetchNextNum } = useQuery({
    queryKey: ['next-inventory-number', prefix],
    queryFn:  () => equipmentAPI.getNextNumber(prefix).then(r => r.data),
    enabled:  open,
  });

  // Когда открывается форма — подставляем номер
  useEffect(() => {
    if (open && nextNumData) {
      form.setFieldsValue({ inventory_number: nextNumData.next });
    }
  }, [open, nextNumData, form]);

  // При смене префикса — обновляем номер
  const handlePrefixChange = (newPrefix) => {
    setPrefix(newPrefix);
    refetchNextNum();
  };

  const createMutation = useMutation({
    mutationFn: (data) => equipmentAPI.create(data),
    onSuccess: () => {
      message.success('Оборудование добавлено');
      form.resetFields();
      onClose();
      queryClient.invalidateQueries(['equipment']);
      queryClient.invalidateQueries(['equipment-stats']);
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const handleSubmit = () => {
    form.validateFields().then(values => {
      createMutation.mutate({
        inventory_number:  values.inventory_number  || null,
        serial_number:     values.serial_number     || null,
        accounting_number: values.accounting_number || null,
        category_id:       values.category_id,
        type_id:           values.type_id,
        name:              values.name,
        status:            values.status || 'На складе',
        location_id:       values.location_id      || null,
        responsible_id:    values.responsible_id    || null,
        company_id:        values.company_id        || null,
        cost:              values.cost ? parseFloat(values.cost) : null,
        purchase_date:     values.purchase_date     || null,
        notes:             values.notes             || null,
      });
    });
  };

  const handleClose = () => {
    form.resetFields();
    setCategoryId(null);
    onClose();
  };

  const locationOptions  = (locationsData || []).map(l => ({ label: l.name, value: l.id }));
  const companyOptions   = (companiesData  || []).map(c => ({ label: c.name, value: c.id }));
  const categoryOptions  = (categoriesData || []).map(c => ({ label: c.name, value: c.id }));
  const typeOptions      = (typesData      || []).map(t => ({ label: t.name, value: t.id }));
  const employeeOptions  = (employeesData?.items || []).map(e => ({
    label: `${e.full_name}${e.job_title ? ` — ${e.job_title}` : ''}`,
    value: e.id,
  }));

  return (
    <Modal
      open={open}
      title="Добавить оборудование"
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={createMutation.isPending}
      okText="Добавить" cancelText="Отмена"
      width={640}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>

        {/* Инвентарный номер с выбором префикса */}
        <Form.Item label="Инвентарный номер">
          <Space.Compact style={{ width: '100%' }}>
            <Select
              value={prefix}
              onChange={handlePrefixChange}
              style={{ width: 80 }}
              options={[
                { label: 'K', value: 'K' },
                { label: 'S', value: 'S' },
              ]}
            />
            <Form.Item name="inventory_number" noStyle>
              <Input
                style={{ flex: 1 }}
                placeholder="Генерируется автоматически"
                addonAfter={
                  <Button
                    size="small" type="text" icon={<SwapOutlined />}
                    onClick={() => refetchNextNum()}
                    title="Обновить номер"
                  />
                }
              />
            </Form.Item>
          </Space.Compact>
          {nextNumData && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              Следующий свободный: {nextNumData.next}
            </Text>
          )}
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="serial_number" label="Серийный номер">
              <Input placeholder="SN123456" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="accounting_number" label="Бухгалтерский номер">
              <Input placeholder="Необязательно" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="status" label="Статус" initialValue="На складе">
              <Select options={Object.keys(STATUS_COLORS).map(s => ({
                label: <Tag color={STATUS_COLORS[s]}>{s}</Tag>, value: s,
              }))} />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '8px 0' }} />

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="category_id" label="Категория" rules={[{ required: true, message: 'Выберите категорию' }]}>
              <Select
                placeholder="Выберите категорию"
                options={categoryOptions}
                onChange={(v) => { setCategoryId(v); form.setFieldValue('type_id', null); }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="type_id" label="Тип" rules={[{ required: true, message: 'Выберите тип' }]}>
              <Select
                placeholder={categoryId ? 'Выберите тип' : 'Сначала выберите категорию'}
                disabled={!categoryId}
                options={typeOptions}
                showSearch
                filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="name" label="Наименование" rules={[{ required: true, message: 'Введите наименование' }]}>
          <Input placeholder="Ноутбук Apple MacBook Pro 14 M3 Pro" />
        </Form.Item>

        <Divider style={{ margin: '8px 0' }} />

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="location_id" label="Место хранения">
              <Select showSearch placeholder="Склад СПб" options={locationOptions}
                filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="company_id" label="Компания">
              <Select placeholder="Киберматч" options={companyOptions} allowClear />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="responsible_id" label="Ответственный">
          <Select showSearch placeholder="Назначить сотрудника (необязательно)" allowClear
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={employeeOptions} />
        </Form.Item>

        <Divider style={{ margin: '8px 0' }} />

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="cost" label="Стоимость (₽)">
              <Input type="number" placeholder="50000" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="purchase_date" label="Дата покупки (ГГГГ-ММ-ДД)">
              <Input placeholder="2024-01-15" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="notes" label="Комментарий">
          <Input.TextArea rows={2} placeholder="Необязательно" />
        </Form.Item>

      </Form>
    </Modal>
  );
}